// Word Chain rules. A chain is a list of words where each word joins the next to make a compound word:
// SUN + FLOWER = SUNFLOWER, FLOWER + POT = FLOWERPOT, POT + LUCK = POTLUCK. The first and last words are shown;
// each hidden word shows its first letter and its length. A hidden word is right when it is the chain's word, or any
// other word of the same length and first letter that makes a compound with both of its neighbours
// (Puzzle.accept, worked out by script/generate-word-chain.ts).

export type WCLevel = "easy" | "medium" | "hard"
export const WC_LEVELS: readonly WCLevel[] = ["easy", "medium", "hard"]
/** Words in the chain, ends included: Easy hides 2, Medium 3, Hard 4. */
export const WC_LENGTH: Record<WCLevel, number> = { easy: 4, medium: 5, hard: 6 }

export interface Puzzle {
  id: string
  level: WCLevel
  /** The chain, upper case, ends included. */
  words: string[]
  /** For each word: every answer accepted in that place (always includes the word itself; ends only themselves). */
  accept: string[][]
}

/** What the player has typed for each word; the ends and the given first letters are filled from the start. */
export type Entries = string[]

export const hidden = (p: Puzzle): number[] => p.words.map((_, i) => i).slice(1, -1)

export function startEntries(p: Puzzle): Entries {
  return p.words.map((w, i) => (i === 0 || i === p.words.length - 1 ? w : w[0]!))
}

export const isFull = (p: Puzzle, e: Entries, i: number): boolean => e[i]!.length === p.words[i]!.length
export const isRight = (p: Puzzle, e: Entries, i: number): boolean => p.accept[i]!.includes(e[i]!)
export const isSolved = (p: Puzzle, e: Entries): boolean => p.words.every((_, i) => isRight(p, e, i))

/** Type one letter into word i; ignored once the word is full. The given first letter can't be changed. */
export function typeLetter(p: Puzzle, e: Entries, i: number, letter: string): Entries {
  if (isFull(p, e, i) || !/^[A-Z]$/.test(letter)) return e
  const next = [...e]
  next[i] = e[i]! + letter
  return next
}

/** Take the last typed letter off word i, never the given first letter. */
export function eraseLetter(e: Entries, i: number): Entries {
  if (e[i]!.length <= 1) return e
  const next = [...e]
  next[i] = e[i]!.slice(0, -1)
  return next
}

/** Reveal the next letter of word i: wrong letters after the given one are replaced by the chain's own. */
export function revealLetter(p: Puzzle, e: Entries, i: number): Entries {
  const word = p.words[i]!
  if (isRight(p, e, i)) return e
  let keep = 0
  while (keep < e[i]!.length && e[i]![keep] === word[keep]) keep++
  const next = [...e]
  next[i] = word.slice(0, Math.min(word.length, keep + 1))
  return next
}

/** The two compound words a solved word i makes with its neighbours, for the announcement ("sunflower and flowerpot"). */
export function compounds(e: Entries, i: number): string[] {
  const out: string[] = []
  if (i > 0) out.push((e[i - 1]! + e[i]!).toLowerCase())
  if (i < e.length - 1) out.push((e[i]! + e[i + 1]!).toLowerCase())
  return out
}

/** Every word accepted in place i of a chain: same length and first letter, a compound with both neighbours. */
export function acceptedAt(chain: string[], i: number, pairs: Set<string>, vocabulary: string[]): string[] {
  const w = chain[i]!
  if (i === 0 || i === chain.length - 1) return [w]
  const alts = vocabulary.filter((x) => x.length === w.length && x[0] === w[0] && !chain.includes(x) &&
    pairs.has(`${chain[i - 1]} ${x}`) && pairs.has(`${x} ${chain[i + 1]}`))
  return [w, ...alts]
}
